import { useQuery } from "@tanstack/react-query"
import { APP_NAME } from "@/lib/appConfig"
import { usePageSEO } from "@/lib/usePageSEO"
import { Map, Mail, ArrowRight, Lightbulb, CheckCircle2, Clock } from "lucide-react"
import { api } from "@/lib/api"
import { format } from "date-fns"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"

interface PublicFeature {
  id: string
  title: string
  description: string | null
  status: "pending" | "planned" | "in_progress" | "completed" | "rejected"
  requested_by_name: string | null
  planned_updates: string | null
  created_at: string
}

export default function PublicRoadmapPage() {
  usePageSEO({
    title: `Product Roadmap | ${APP_NAME}`,
    description: `See what's coming next on ${APP_NAME}. Follow our public product roadmap and submit feature requests.`,
    path: "/roadmap",
  })

  const { data: features = [], isLoading } = useQuery<PublicFeature[]>({
    queryKey: ["public", "feature-requests"],
    queryFn: async () => {
      const { data } = await api.get("/api/public/feature-requests")
      return data.data ?? []
    },
  })

  // Group by status
  const inProgress = features.filter((f) => f.status === "in_progress")
  const planned = features.filter((f) => f.status === "planned")
  const completed = features.filter((f) => f.status === "completed")

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-muted/30 border-b border-border py-16 md:py-24">
        <div className="container px-4 md:px-6 max-w-[1000px] mx-auto text-center space-y-6">
          <Badge variant="secondary" className="px-3 py-1 font-semibold rounded-full mb-4">
            <Map className="h-4 w-4 mr-2" />
            Product Roadmap
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
            Building the future of <span className="text-primary">RentKeep</span> together.
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-[700px] mx-auto leading-relaxed">
            See what we're working on, what's coming next, and track the features suggested by our amazing community of landlords and property managers.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="container px-4 md:px-6 py-12 md:py-20 max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          
          {/* Main Roadmap Columns */}
          <div className="lg:col-span-2 space-y-16">
            
            {/* In Progress */}
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-amber-100 text-amber-600">
                  <Clock className="h-5 w-5" />
                </div>
                <h2 className="text-2xl font-bold">In Progress</h2>
                <Badge variant="secondary" className="ml-auto rounded-full">{inProgress.length}</Badge>
              </div>
              <Separator />
              <div className="grid gap-4">
                {inProgress.length > 0 ? (
                  inProgress.map((f) => <FeatureCard key={f.id} feature={f} />)
                ) : (
                  <p className="text-muted-foreground text-sm italic">No features currently in development.</p>
                )}
              </div>
            </div>

            {/* Planned */}
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-indigo-100 text-indigo-600">
                  <Lightbulb className="h-5 w-5" />
                </div>
                <h2 className="text-2xl font-bold">Planned</h2>
                <Badge variant="secondary" className="ml-auto rounded-full">{planned.length}</Badge>
              </div>
              <Separator />
              <div className="grid gap-4">
                {planned.length > 0 ? (
                  planned.map((f) => <FeatureCard key={f.id} feature={f} />)
                ) : (
                  <p className="text-muted-foreground text-sm italic">No planned features at the moment.</p>
                )}
              </div>
            </div>

            {/* Completed */}
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-emerald-100 text-emerald-600">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <h2 className="text-2xl font-bold">Recently Completed</h2>
                <Badge variant="secondary" className="ml-auto rounded-full">{completed.length}</Badge>
              </div>
              <Separator />
              <div className="grid gap-4">
                {completed.length > 0 ? (
                  completed.map((f) => <FeatureCard key={f.id} feature={f} />)
                ) : (
                  <p className="text-muted-foreground text-sm italic">Check back soon for completed features.</p>
                )}
              </div>
            </div>

          </div>

          {/* Sidebar / Widget */}
          <div className="space-y-6">
            <Card className="sticky top-24 border-border shadow-sm rounded-3xl overflow-hidden">
              <div className="bg-muted/30 p-6 border-b border-border text-center">
                <div className="h-12 w-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-6 w-6" />
                </div>
                <CardTitle className="text-xl">Request a Feature</CardTitle>
                <CardDescription className="mt-2 text-sm leading-relaxed">
                  Have an idea that could make RentKeep better for everyone? We want to hear it!
                </CardDescription>
              </div>
              <CardContent className="p-6 space-y-4">
                <p className="text-sm text-muted-foreground">
                  To ensure we provide the best support and keep our roadmap focused, we handle all feature requests directly via email. 
                </p>
                <div className="bg-muted/50 p-4 rounded-xl border border-border/50 text-center">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Email us at</p>
                  <a href="mailto:support@rent-keep.com?subject=Feature Request" className="font-semibold text-foreground hover:text-primary transition-colors">
                    support@rent-keep.com
                  </a>
                </div>
                <div className="pt-2">
                  <a href="mailto:support@rent-keep.com?subject=Feature Request" className="w-full flex items-center justify-center h-10 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors">
                    Send us an email
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>

        </div>
      </section>
    </div>
  )
}

function FeatureCard({ feature }: { feature: PublicFeature }) {
  return (
    <Card className="rounded-2xl border border-border/60 shadow-sm transition-all hover:shadow-md hover:border-border">
      <CardHeader className="p-5 pb-3">
        <div className="flex justify-between items-start gap-4">
          <div>
            <CardTitle className="text-lg leading-tight">{feature.title}</CardTitle>
            {feature.requested_by_name && (
              <p className="text-xs text-muted-foreground mt-1.5 font-medium">
                Suggested by {feature.requested_by_name}
              </p>
            )}
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap bg-muted px-2 py-1 rounded-md font-medium">
            {format(new Date(feature.created_at), "MMM yyyy")}
          </span>
        </div>
      </CardHeader>
      {(feature.description || feature.planned_updates) && (
        <CardContent className="p-5 pt-0 space-y-4">
          {feature.description && (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {feature.description}
            </p>
          )}
          
          {feature.planned_updates && (
            <div className="bg-muted/30 p-3 rounded-xl border border-border/50">
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Updates</p>
              <p className="text-sm text-foreground">{feature.planned_updates}</p>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}
